import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Copy, Download, Zap, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { geminiService, type N8nWorkflow } from '@/services/geminiService';
import ApiKeyInput from './ApiKeyInput';

interface AutomationGeneratorProps {
  initialPrompt?: string;
}

const AutomationGenerator = ({ initialPrompt = '' }: AutomationGeneratorProps) => {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [generatedWorkflow, setGeneratedWorkflow] = useState<N8nWorkflow | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempApiKey, setTempApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);

  // Debug environment variables and check if we need API key input
  useEffect(() => {
    const hasEnvKey = !!import.meta.env.VITE_GEMINI_API_KEY;
    console.log('🔍 Environment Debug:', {
      allEnvVars: Object.keys(import.meta.env),
      viteGeminiKey: hasEnvKey,
      viteGeminiKeyLength: (import.meta.env.VITE_GEMINI_API_KEY || '').length
    });
    
    // Show API key input if environment variable is not available
    setShowApiKeyInput(!hasEnvKey);
  }, []);

  // Update prompt when initialPrompt changes
  useEffect(() => {
    if (initialPrompt) {
      setPrompt(initialPrompt);
      setError(null);
      setGeneratedWorkflow(null);
    }
  }, [initialPrompt]);
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description for your automation', {
        description: 'Describe what you want to automate in plain English.'
      });
      return;
    }

    // Check if we have an API key available
    const hasApiKey = import.meta.env.VITE_GEMINI_API_KEY || tempApiKey;
    if (!hasApiKey) {
      setShowApiKeyInput(true);
      toast.error('API key required', {
        description: 'Please enter your Gemini API key to generate workflows.'
      });
      return;
    }
    setIsGenerating(true);
    setError(null);
    console.log('🚀 Starting workflow generation...');
    try {
      const workflow = await geminiService.generateWorkflow(prompt, tempApiKey);
      setGeneratedWorkflow(workflow);
      toast.success('Automation generated successfully! 🎉', {
        description: `Created "${workflow.name}" with ${workflow.nodes.length} nodes.`
      });
      console.log('✅ Workflow generation completed');
    } catch (error) {
      console.error('❌ Workflow generation failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate workflow';
      setError(errorMessage);
      toast.error('Generation failed', {
        description: errorMessage
      });
      
      // If it's an API key error, show the input again
      if (errorMessage.includes('API key')) {
        setShowApiKeyInput(true);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedWorkflow) {
      const jsonString = JSON.stringify(generatedWorkflow, null, 2);
      navigator.clipboard.writeText(jsonString);
      toast.success('Workflow copied to clipboard! 📋', {
        description: 'Ready to import into your n8n instance.'
      });
    }
  };

  const handleDownload = () => {
    if (generatedWorkflow) {
      const jsonString = JSON.stringify(generatedWorkflow, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${generatedWorkflow.name.toLowerCase().replace(/\s+/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Workflow downloaded! 📥', {
        description: 'Import this file into your n8n instance.'
      });
    }
  };

  const examplePrompts = [
    "When I receive an email from a customer, extract the key information, save it to a Google Sheet, and send a Slack notification to the team",
    "Every Monday at 9 AM, fetch data from our API, process it, and send a summary report via email",
    "When a new form is submitted on our website, validate the data, create a new record in our database, and send a welcome email",
    "Monitor our RSS feed for new articles, summarize them using AI, and post to our Discord channel"
  ];

  const handleApiKeySet = (apiKey: string) => {
    setTempApiKey(apiKey);
    setShowApiKeyInput(false);
    toast.success('API key set successfully! 🔑', {
      description: 'You can now generate workflows.'
    });
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Input Section */}
      <Card className="glass-effect border-n8n-200/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="w-6 h-6 text-n8n-500" />
            Describe Your Automation
          </CardTitle>
          <CardDescription className="text-base">
            Tell us what you want to automate in plain English, and our AI will generate the n8n workflow for you.
            {initialPrompt && (
              <span className="block mt-2 text-n8n-600 dark:text-n8n-400 font-medium">
                ✨ Template loaded! You can customize it or generate as-is.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Textarea
            placeholder="E.g., When I receive an email from a customer, extract the key information, save it to a Google Sheet, and send a Slack notification to the team..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="min-h-32 resize-none border-n8n-200 focus:border-n8n-400 focus:ring-n8n-400"
          />
          
          {/* Example Prompts - only show if no initial prompt */}
          {!initialPrompt && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">Try these examples:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {examplePrompts.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setPrompt(example)}
                    className="text-left p-3 text-sm bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-700 transition-colors"
                  >
                    "{example}"
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">Email automation</Badge>
            <Badge variant="secondary" className="text-xs">Data processing</Badge>
            <Badge variant="secondary" className="text-xs">Notifications</Badge>
            <Badge variant="secondary" className="text-xs">API integrations</Badge>
            <Badge variant="secondary" className="text-xs">Webhooks</Badge>
            <Badge variant="secondary" className="text-xs">Scheduling</Badge>
          </div>
          
          <Button 
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            className="btn-gradient text-white px-8 py-2 h-12 text-base font-medium w-full sm:w-auto"
          >
            {isGenerating ? (
              <>
                <Zap className="w-4 h-4 mr-2 animate-spin" />
                Generating with Gemini AI...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate n8n Automation
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Error Section */}
      {error && (
        <Card className="border-red-200 dark:border-red-800 animate-slide-up">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Generation Failed</span>
            </div>
            <p className="text-red-600 dark:text-red-400 mt-2">{error}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Please check your API key configuration or try a different prompt.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Output Section */}
      {generatedWorkflow && (
        <Card className="animate-slide-up">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  {generatedWorkflow.name}
                </CardTitle>
                <CardDescription>
                  Ready to import into your n8n instance • {generatedWorkflow.nodes.length} nodes • Generated by Gemini AI
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy JSON
                </Button>
                <Button
                  onClick={handleDownload}
                  className="gap-2 btn-gradient text-white"
                  size="sm"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Workflow Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-n8n-600">{generatedWorkflow.nodes.length}</div>
                  <div className="text-sm text-muted-foreground">Nodes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-n8n-600">
                    {Object.keys(generatedWorkflow.connections || {}).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Connections</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">n8n</div>
                  <div className="text-sm text-muted-foreground">Format</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">✓</div>
                  <div className="text-sm text-muted-foreground">Ready</div>
                </div>
              </div>

              {/* Import Instructions */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  How to import into n8n:
                </h4>
                <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>1. Copy the JSON or download the file</li>
                  <li>2. Open your n8n instance</li>
                  <li>3. Click "Import from File" or "Import from Clipboard"</li>
                  <li>4. Paste the JSON or select the downloaded file</li>
                  <li>5. Configure any required credentials and activate!</li>
                </ol>
              </div>

              {/* JSON Output */}
              <pre className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg overflow-auto max-h-96 text-sm border">
                <code>{JSON.stringify(generatedWorkflow, null, 2)}</code>
              </pre>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AutomationGenerator;